import { Component, OnInit, inject, input, output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';

/** Supported field types for the generic filter bar. */
export type FilterFieldType = 'text' | 'select' | 'date' | 'daterange';

/** One option inside a `select` field. */
export interface FilterFieldOption {
  value: string;
  label: string;
}

/**
 * Descriptor for a single filter field rendered by `FilterBarComponent`.
 *
 * - `key`: FormControl name (for `daterange`, the "from" control).
 * - `keyTo`: FormControl name for the "to" control of a `daterange` field.
 * - `defaultValue`: Initial value; defaults to `''` when omitted.
 * - `clearValue`: Value restored by the "Limpar" button; defaults to `defaultValue` when
 *   omitted. Use when the initial landing filter differs from "no filter" (ex.: status
 *   defaults to a specific value on load, but Limpar should go back to "Todos").
 */
export interface FilterField {
  key: string;
  label: string;
  type: FilterFieldType;
  placeholder?: string;
  options?: FilterFieldOption[];
  defaultValue?: string;
  clearValue?: string;
  keyTo?: string;
}

@Component({
  selector: 'app-filter-bar',
  standalone: true,
  imports: [ReactiveFormsModule],
  templateUrl: './filter-bar.component.html',
  styleUrl: './filter-bar.component.scss',
})
export class FilterBarComponent implements OnInit {
  private readonly fb = inject(FormBuilder);

  /**
   * Field descriptors that drive the rendered form controls.
   * At minimum provide `key`, `label`, and `type`.
   */
  readonly fields = input.required<readonly FilterField[]>();

  /** Emits the full form value as a flat record whenever the user submits or clears. */
  readonly filtersChange = output<Record<string, string>>();

  form!: FormGroup;

  ngOnInit(): void {
    this.buildForm();
    this.emitCurrent();
  }

  /** Submits the current form values. */
  buscar(): void {
    this.emitCurrent();
  }

  /**
   * Resets every control to its `clearValue` (or `defaultValue`, or `''`) without
   * emitting — só limpa os campos; a busca só é refeita quando o usuário clica em Buscar.
   */
  limpar(): void {
    const defaults = this.buildDefaults((field) => field.clearValue ?? field.defaultValue);
    this.form.reset(defaults);
  }

  // ── private helpers ──────────────────────────────────────────────────────────

  private buildForm(): void {
    const controls: Record<string, string> = this.buildDefaults((field) => field.defaultValue);
    this.form = this.fb.nonNullable.group(controls);
  }

  private buildDefaults(resolveValue: (field: FilterField) => string | undefined): Record<string, string> {
    const defaults: Record<string, string> = {};
    for (const field of this.fields()) {
      defaults[field.key] = resolveValue(field) ?? '';
      if (field.type === 'daterange' && field.keyTo) {
        defaults[field.keyTo] = '';
      }
    }
    return defaults;
  }

  private emitCurrent(): void {
    this.filtersChange.emit(this.form.getRawValue() as Record<string, string>);
  }
}
